// Third-party packages
import {
	extractEmailAndUsername,
} from '@helpers/emailExtraction';
import {
	getIdentApiUrl,
} from '@helpers/getIdentApiUrl';
import {
	getGroups,
	getOrganization,
} from '@helpers/getOrganizations';
import {
	isOnline,
} from '@helpers/networkStatus';

// Internal libraries (@stonal-tech/*)

// Project modules
import {
	logger,
} from '@config/logger';
import {
	AppDescription,
} from '@local_types/index';

// Relative imports
import {
	getAppIdentConfig,
} from '../config/appIdentConfigs';
import {
	ATTEMPT_RESET_PERIOD,
	EXPIRATION,
	MAX_RETRY_ATTEMPTS,
	RETRY_INTERVALS,
} from '../config/identCheck';
import {
	STORAGE_KEY_ID_ATTEMPTS,
	STORAGE_KEY_ID_CONFIRMED,
	STORAGE_KEY_ID_FIRST_ATTEMPT,
	STORAGE_KEY_ID_LAST,
	STORAGE_KEY_IDENTIFIED_AS,
	STORAGE_KEY_USER_DATA,
} from '../config/storageKeys';
import {
	Analytics,
} from './analytics';

type OIDCProfile = {
	sub: string;
	email: string;
	email_verified: boolean;
	name: string;
	given_name: string;
};

type OIDCCredentials = {
	access_token: string;
	expires_at: number;
	id_token: string;
	profile: OIDCProfile;
	refresh_token: string;
	scope: string;
	session_state: string;
	token_type: string;
	key: string;
};

const INTERNAL_DOMAINS = [
	'stonal.com',
	'lafoncierenumerique.com',
];

export class IdentCheck {
	private passNb: number;
	private app: AppDescription;
	private analytics: Analytics;
	constructor(analytics: Analytics, app: AppDescription) {
		this.passNb = 0;
		this.app = app;
		this.analytics = analytics;

		// Listen for auth-ready event from lib-authentication-react
		// Triggers immediate identification when OIDC token exchange completes
		window.addEventListener('stn:auth-ready', () => {
			logger.info('[IdentCheck] Received stn:auth-ready event, triggering identification');
			this.run();
		}, {
			once: true,
		});
	}

	/**
	 * Emits the identify call for a user.
	 *
	 * The organization data source is deliberately NOT a trait. It describes the
	 * organization being viewed, not the user, and it changes under the same user on
	 * an organization switch — where identify is never re-emitted. It is injected as
	 * the `org_data_source` event property by `Analytics._extendProperties` instead.
	 *
	 * @param userData - Raw payload of the identification API
	 */
	identifyFromUserData(userData: any) {
		if (!userData) {
			logger.warn('No user data provided, can\'t identify user');
			return;
		}

		if (!userData.uid && userData.id) {
			userData.uid = userData.id;
		}

		const now = new Date().getTime().toString();

		const {
			firstName,
			lastName,
		} = userData;

		// Extract proper email and username using intelligent detection
		const {
			email, username,
		} = extractEmailAndUsername(userData);

		const emailDomain = email?.split('@')[1]?.toLowerCase();
		const isInternal = INTERNAL_DOMAINS.some((d) => emailDomain === d);

		const traits: Record<string, any> = {
			email,
			firstName,
			internal: isInternal,
			lastName,
			navigatorHardwareConcurrency: navigator.hardwareConcurrency,
			username,
		};

		// We try to extract the organization using app-specific logic
		const appConfig = getAppIdentConfig(this.app.code);
		const organization = getOrganization(userData, appConfig);

		if (organization) {
			traits.organization = organization;
		}

		// User groups by org — always included, even if empty
		traits.userGroups = getGroups(userData, appConfig);

		this.analytics.identify(userData.uid, traits);
		localStorage.setItem(STORAGE_KEY_IDENTIFIED_AS, userData.uid);
		localStorage.setItem(STORAGE_KEY_ID_LAST, now);
		// Cache full user data for restoration on subsequent page loads
		try {
			localStorage.setItem(STORAGE_KEY_USER_DATA, JSON.stringify(userData));
		} catch { /* ignore quota errors */ }
		// Mark identification as successfully confirmed
		localStorage.setItem(STORAGE_KEY_ID_CONFIRMED, now);
		// Reset retry attempts counter
		localStorage.removeItem(STORAGE_KEY_ID_ATTEMPTS);
		localStorage.removeItem(STORAGE_KEY_ID_FIRST_ATTEMPT);

	}

	private _restoreFromCache(): boolean {
		try {
			const cached = localStorage.getItem(STORAGE_KEY_USER_DATA);
			logger.debug('[IdentCheck] _restoreFromCache called', {
				hasCachedData: !!cached,
			});
			if (cached) {
				const userData = JSON.parse(cached);
				this.identifyFromUserData(userData);
				logger.debug('[IdentCheck] _restoreFromCache: identifyFromUserData completed');
				return true;
			}
		} catch (e) {
			logger.error('[IdentCheck] _restoreFromCache failed:', e as Error);
		}
		return false;
	}

	async run() {
		this.passNb++;

		// Skip identification when offline to avoid futile network requests and error logs
		if (!isOnline()) {
			logger.info('[IdentCheck] Offline, skipping identification attempt');
			setTimeout(async () => await this.run(), RETRY_INTERVALS[0]);
			return;
		}

		const now = new Date().getTime();

		// If we are already identified, restore from cache and skip API call
		// Fall through to API call if cache is missing (upgrade from older version)
		const lastIdentified = window.localStorage.getItem(STORAGE_KEY_ID_LAST);
		if (lastIdentified && parseInt(lastIdentified) > now - EXPIRATION) {
			if (this._restoreFromCache()) return;
		}

		// Check if identification has ever been confirmed
		const idConfirmed = window.localStorage.getItem(STORAGE_KEY_ID_CONFIRMED);
		if (idConfirmed) {
			// Successfully identified before — restore from cache to populate traits
			if (this._restoreFromCache()) return;
		}

		// Get retry attempt tracking
		const attemptsStr = window.localStorage.getItem(STORAGE_KEY_ID_ATTEMPTS);
		const firstAttemptStr = window.localStorage.getItem(STORAGE_KEY_ID_FIRST_ATTEMPT);

		let attempts = attemptsStr ? parseInt(attemptsStr, 10) : 0;
		const firstAttempt = firstAttemptStr ? parseInt(firstAttemptStr, 10) : now;

		// Reset attempts on each page load if identification was never confirmed
		// Previous failures (CORS, consent, infra) should not permanently block identification
		if (!idConfirmed && this.passNb <= 2) {
			attempts = 0;
			window.localStorage.removeItem(STORAGE_KEY_ID_ATTEMPTS);
			window.localStorage.removeItem(STORAGE_KEY_ID_FIRST_ATTEMPT);
		}

		// Reset attempts if 24 hours have passed since first attempt
		if (now - firstAttempt > ATTEMPT_RESET_PERIOD) {
			attempts = 0;
			window.localStorage.removeItem(STORAGE_KEY_ID_ATTEMPTS);
			window.localStorage.removeItem(STORAGE_KEY_ID_FIRST_ATTEMPT);
		}

		// Check if we've exceeded max retry attempts
		if (attempts >= MAX_RETRY_ATTEMPTS) {
			// Max attempts reached, don't retry anymore

			return;
		}

		// Use progressive backoff based on attempt number
		const idCheckPeriod = RETRY_INTERVALS[Math.min(attempts, RETRY_INTERVALS.length - 1)];

		this.passNb++;

		if (this.passNb % 10 == 0) {
			const shouldGivingUp = this.passNb > 100;
			if (shouldGivingUp) {
				return;
			}
		}

		// Read all "oidc.user:*" credentials from the sessionStorage
		const oidcKeys = Object.keys(window.sessionStorage).filter((key) => key.startsWith('oidc.user:'));
		logger.info('[IdentCheck] OIDC keys found in sessionStorage:', {
			count: oidcKeys.length,
			keys: oidcKeys,
		});

		const oidcCredentials = oidcKeys
			.map((key) => {
				const value = window.sessionStorage.getItem(key);
				if (!value) return null;
				try {
					const parsed = JSON.parse(value);
					// Handle different OIDC client library formats
					// Some libs store tokens at root, others in authnResult/authResult
					const tokenData = parsed.authnResult || parsed.authResult || parsed;

					logger.info('[IdentCheck] Processing OIDC credential:', {
						hasAccessTokenInRoot: !!parsed.access_token,
						hasAccessTokenInTokenData: !!tokenData.access_token,
						hasAuthnResult: !!parsed.authnResult,
						hasAuthResult: !!parsed.authResult,
						key,
					});

					if (!tokenData.access_token) {
						logger.warn('[IdentCheck] No access_token found in OIDC credential:', {
							key,
						});
						return null;
					}

					return {
						...tokenData,
						key,
					} as OIDCCredentials;
				} catch (e) {
					logger.error('Failed to parse OIDC credentials from sessionStorage:', {
						e,
						value,
					});
					return null;
				}
			}).filter((cred): cred is OIDCCredentials => cred !== null && !!cred.access_token);

		logger.info('[IdentCheck] Valid OIDC credentials after filtering:', {
			count: oidcCredentials.length,
		});

		// Call the ident API
		for (const oidcCredential of oidcCredentials) {
			const identApiUrl = getIdentApiUrl(this.app);
			logger.info('[IdentCheck] Calling OIDC ident API:', {
				key: oidcCredential.key,
				url: identApiUrl,
			});

			const identApiResponse = await fetch(identApiUrl, {
				headers: {
					'Authorization': `Bearer ${oidcCredential.access_token}`,
				},
			});

			logger.info('[IdentCheck] OIDC API response:', {
				ok: identApiResponse.ok,
				status: identApiResponse.status,
				url: identApiUrl,
			});

			if (!identApiResponse.ok) {
				logger.warn(`Ident API call failed on ${oidcCredential.key}: ${identApiResponse.status} ${identApiResponse.statusText}`);
				continue;
			}
			const identApiData = await identApiResponse.json();

			logger.info('[IdentCheck] OIDC API data received:', {
				data: identApiData,
				hasId: !!identApiData?.id,
				hasUid: !!identApiData?.uid,
			});

			if (identApiData && identApiData.uid) {
				logger.info('[IdentCheck] OIDC identification successful, calling identifyFromUserData');
				this.identifyFromUserData(identApiData);
				return;
			} else {
				logger.warn('[IdentCheck] OIDC API returned data without uid:', {
					identApiData,
				});
			}
		}

		logger.info('[IdentCheck] OIDC identification complete, no valid credentials or API calls succeeded');

		// Track if any authentication method was attempted
		let hasAuthAttempted = oidcCredentials.length > 0;

		// Try fallback API even if OIDC failed
		// Use case: OIDC token expired but session cookies still valid
		// This provides a more robust fallback mechanism
		const config = getAppIdentConfig(this.app.code);

		logger.info('[IdentCheck] Fallback API configuration:', {
			apiUrl: config.apiUrl,
			appCode: this.app.code,
			hasApiUrl: !!config.apiUrl,
			oidcAttempted: hasAuthAttempted,
		});

		if (config.apiUrl) {
			// Fallback API is available - try it even if OIDC was attempted
			if (hasAuthAttempted) {
				logger.info('[IdentCheck] OIDC failed, attempting fallback API as backup', {
					reason: 'OIDC token may be expired while session cookies still valid',
				});
			}

			hasAuthAttempted = true; // Mark that fallback is being attempted

			try {
				logger.info('[IdentCheck] Calling fallback API:', {
					appCode: this.app.code,
					url: config.apiUrl,
				});

				const response = await fetch(config.apiUrl, {
					redirect: 'manual',
				});

				// Redirect to a different origin (e.g., platform.stonal.io) — skip silently
				if (response.type === 'opaqueredirect') {
					logger.info('[IdentCheck] Fallback API redirected to different origin, skipping');
					hasAuthAttempted = false;
				} else if (response.ok) {
					const rawData = await response.json();

					logger.info('[IdentCheck] Fallback API data received:', {
						rawData,
					});

					// Apply app-specific data extraction
					const userData = config.extractUserData ?
						config.extractUserData(rawData) :
						rawData;

					logger.info('[IdentCheck] Extracted user data:', {
						hasExtractFunction: !!config.extractUserData,
						userData,
					});

					// Validate and identify if data is valid
					if (config.validateUserData && config.validateUserData(userData)) {
						logger.info('[IdentCheck] User data validated, calling identifyFromUserData');
						this.identifyFromUserData(userData);
						return; // Success! Don't retry
					} else {
						logger.warn('[IdentCheck] User data validation failed:', {
							userData,
						});
					}
				} else {
					logger.warn('[IdentCheck] Fallback API returned non-OK status:', {
						status: response.status,
						statusText: response.statusText,
					});
				}
			} catch (e) {
				logger.error('[IdentCheck] Failed to fetch user data from fallback API:', e as Error);
			}
		}

		// Increment retry attempts only if at least one auth method was attempted
		// If no auth method available yet, don't increment (still waiting for auth to be ready)
		if (hasAuthAttempted) {
			logger.info('[IdentCheck] Authentication was attempted but failed, incrementing retry counter');
			if (!attemptsStr) {
				// First attempt - record the timestamp
				window.localStorage.setItem(STORAGE_KEY_ID_FIRST_ATTEMPT, now.toString());
			}
			window.localStorage.setItem(STORAGE_KEY_ID_ATTEMPTS, (attempts + 1).toString());
		} else {
			logger.info('[IdentCheck] No authentication method available yet, will retry without incrementing counter');
		}

		setTimeout(async () => await this.run(), idCheckPeriod);
	}
}
